// 78 §4 (AUTH-1, AUTH-11) — THE MODAL, BUILT ONCE.
//
// [RULED] "Three mutually distinguishable login shapes, ONE MODAL, one look and
//          feel. Sequencing across PRs is permitted; three different
//          look-and-feels are not."
//
// So this is the modal, with one shape in it. The shapes that follow — passkeys,
// passwordless — are ADDED HERE: an entry in `src/lib/authShapes.ts`, its four
// texts, and a branch in `renderChallenge`. They do not arrive with a second
// modal, and nothing below is TOTP-specific except that one branch and the
// enrolment panel it names.
//
// FOUR STEPS, one chrome:
//   'challenge'  sign in — the six digits, or a recovery code
//   'enrol'      the square code, then one code typed back to confirm it
//   'codes'      the ten recovery codes, shown ONCE
//   'about'      AUTH-11's four texts, reachable from every other step
//
// WHY THE SQUARE CODE IS DRAWN AND NOT FETCHED AS AN IMAGE. The API answers the
// QR MODULES (a matrix of 0/1 rows) and this draws `<rect>` elements from them.
// Not `dangerouslySetInnerHTML` with the encoder's SVG string — that is a sink
// on `scripts/lint/html-sinks-sandboxed.js`'s baseline, which may shrink and
// never grow — and not a `data:` image either, which would put the encoder in
// the browser bundle. Real elements, no parser, no bundle growth.
//
// The word "QR" appears NOWHERE a person can read. Laurent's own English in
// §4 says "a square code", and that is kept verbatim — the term is a registered
// trademark of DENSO WAVE (the notice ships in the encoder's own header; see
// docs/dependency-audit-2026-08-13-totp.md §2b).
import { useState, useEffect, useRef } from "react";
import { createPasskey, assertPasskey, passkeyCapability, classifyFailure } from "../lib/webauthnClient";
import type { RequestOptionsJSON, PasskeyCapability, PasskeyFailure } from "../lib/webauthnClient";
import { useTranslation } from "../i18n";
import { FaceGlyph, FingerprintGlyph } from "./AuthGlyphs";
import { CT, RAD, FS, Z } from "../lib/tokens";
import {
  secondStep as apiSecondStep,
  enrolTotpStart,
  enrolTotpConfirm,
  enrolPasskeyStart,
  enrolPasskeyConfirm,
  secondStepPasskey,
} from "../api";
import type {
  AuthUser,
  RecoveryCount,
  QrMatrix,
  EnrolStartResponse,
  PasskeyStartResponse,
  EnrolConfirmResponse,
} from "../api";
import type { AuthConfigLike } from "../lib/authShapes";
import {
  AUTH_SHAPES,
  shapeById,
  installationTier,
  sellerFallbackKey,
} from "../lib/authShapes";
import type { AuthShape, InstallationTier } from "../lib/authShapes";

type Step = "challenge" | "enrol" | "codes" | "about";

/** What either `start` call answers — the square code's, or the passkey's. */
type EnrolStart = EnrolStartResponse | PasskeyStartResponse;

/**
 * Narrow the union by what the response CARRIES rather than by which shape the
 * caller believes it asked for. The two can disagree — a stale response landing
 * after a shape change — and the renderer must follow the object it actually
 * holds.
 */
const isSquareCode = (e: EnrolStart | null): e is EnrolStartResponse =>
  !!e && "qr" in e;

/** Narrow enough to type the calls below, wide enough not to fight the i18n resolver. */
type Translate = (key: string, vars?: Record<string, unknown>) => string;

interface AuthStepModalProps {
  /**
   * 'enrol' opens on the square code (AUTH-7's forced path, and 80 §1's card);
   * 'challenge' asks for a code; 'codes' opens straight onto the ten.
   *
   * 80 §1 — "the same step; do not build a second one." Re-issuing recovery
   * codes from Settings produces exactly what enrolment produces: ten codes,
   * shown once, with the same words and the same print button. So the mode is
   * WIDENED to open on that step rather than a second screen being drawn that
   * says the same thing in its own way (AUTH-1, and THE FOUR RULES §4).
   */
  mode: "enrol" | "challenge" | "codes";
  /** What a verified password bought. Never a session — see lib/authChallenge.js. */
  challenge?: string;
  /** Which shapes this person may answer with; the first is offered. */
  shapes?: string[];
  recoveryRemaining?: RecoveryCount | null;
  /** 83 §5 — the server's own name for this browser, to pre-fill the trust box. */
  suggestedDeviceName?: string | null;
  /**
   * 82 §4 — the assertion options the login response carried, when the person
   * holds a passkey. Present is the whole signal: a passkey cannot be typed, so
   * the challenge step offers a button instead of an input when this is here.
   */
  webauthn?: RequestOptionsJSON | null;
  cfg?: AuthConfigLike | null;
  /**
   * The ten, when the caller already has them — mode 'codes' only. They arrive
   * from `POST /recovery-codes/regenerate`, which answers them ONCE, so this
   * modal is where they are read and there is nowhere to fetch them again.
   */
  initialCodes?: string[] | null;
  /** How many codes the call that produced `initialCodes` destroyed. */
  initialReplaced?: number;
  onDone?: (user?: AuthUser, token?: string, res?: unknown) => void;
  onCancel?: () => void;
}

// ── 82k — WHAT WILL BE ASKED OF YOU, DRAWN ─────────────────────────────────
//
// A person about to press "Sign in with passkey" does not know whether the
// machine will look at their face, ask for a finger, or want a plugged-in key.
// The two glyphs say it before the system dialog appears.
// 83 — the two glyphs moved to `AuthGlyphs.tsx` UNCHANGED, so the settings
// panel shows the same marks for the same thing. Extract, don't improve.

interface AboutProps {
  /**
   * Every shape this screen is offering — AUTH-11's four texts belong to each
   * of them, and a person holding two who clicks "How does this work?" is
   * asking about both. Showing only the first would answer half the question
   * with no sign that the other half exists.
   */
  shapes: AuthShape[];
  tier: InstallationTier;
  t: Translate;
  onBack: () => void;
}

export default function AuthStepModal({
  mode,
  challenge,
  shapes = [],
  recoveryRemaining = null,
  webauthn = null,
  // 83 §5 (glance) — what to pre-fill the browser-name box with. Computed
  // SERVER-SIDE from the request's user-agent and carried on the challenge
  // response, so there is no second `labelFor` in TypeScript to drift from it.
  suggestedDeviceName = null,
  cfg = null,
  initialCodes = null,
  initialReplaced = 0,
  onDone,
  onCancel,
}: AuthStepModalProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>(mode === "challenge" ? "challenge" : mode);
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  // 82 §4 — one state for both starts. A union rather than a second piece of
  // state: `step === "enrol"` means "an enrolment is in flight or ready", and
  // which SHAPE it is is already known from `shapes`. Two fields would let the
  // two disagree.
  const [enrolment, setEnrolment] = useState<EnrolStart | null>(null);
  // Seeded from the prop, because mode 'codes' is mounted BY the caller that
  // already holds them. There is no effect to fetch them and there must not be:
  // the server answers them once.
  const [codes, setCodes] = useState<string[] | null>(initialCodes);
  // 82a's glance, finding 2 — HOW MANY THE NEW TEN JUST KILLED. Zero on a first
  // enrolment, ten on any later one, and seeded from the prop for mode 'codes'
  // because the caller there already made the call.
  const [replaced, setReplaced] = useState<number>(initialReplaced || 0);
  // ── 82j — THE FAILURE PATH IS THE DESIGN ──────────────────────────────────
  //
  // `note` is deliberately NOT `err`. A person who pressed Cancel in the
  // operating system's own dialog did nothing wrong and must not be shown a red
  // box; the same is true of the case we cannot tell apart from it. So the
  // broad class renders as a calm line that points at the other ways in, and
  // the red box is kept for faults.
  const [note, setNote] = useState("");
  // What this machine could answer BEFORE anybody presses anything. Starts null
  // — "not asked yet" — so the control is never refused on an unanswered
  // question.
  const [capability, setCapability] = useState<PasskeyCapability | null>(null);
  const [session, setSession] = useState<EnrolConfirmResponse | null>(null);
  const [cameFrom, setCameFrom] = useState<Step>("challenge");
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Both refs exist so the enrol effect below can READ them without DEPENDING
  // on them — see the comment there; a dependency is what re-enters the effect.
  const enrolmentRef = useRef<EnrolStart | null>(null);
  const tRef = useRef(t);
  tRef.current = t;

  // ── 82a's GLANCE, FINDING 1 — TWO SHAPES ARE TWO ANSWERS ──────────────────
  //
  // `shapes[0]` was ONE variable doing TWO jobs, and the two disagree the moment
  // somebody holds more than one shape:
  //
  //   · the ENROL step enrols exactly ONE shape — whichever the card's button
  //     named — and there `shapes[0]` is right;
  //   · the CHALLENGE step must offer EVERY shape the person holds, because
  //     AUTH-5 says "a user may hold several shapes at once, AND ANY ENROLLED
  //     SHAPE SIGNS HIM IN".
  //
  // Taking `shapes[0]` at the challenge step made the second shape UNREACHABLE:
  // with TOTP and a passkey both enrolled, AUTH_SHAPES is ordered totp-first, so
  // sign-in always drew the six digits and offered no way to answer with the
  // passkey. Briefs 78 and 80 could not have seen it — it appears only once
  // somebody holds two, and until 82 nobody could.
  const offered = shapes.map((id) => shapeById(id)).filter((s): s is AuthShape => !!s);
  const shape = offered[0] || AUTH_SHAPES[0];
  // 82 §4 — ONE modal, one branch. `isPasskey` is the only thing that differs
  // between the two shapes on this screen: which pair of calls runs, and whether
  // the person types or presses. Every style, every heading, every fallback
  // text and the whole recovery-code path are shared, which is AUTH-1 kept by
  // construction rather than by intention.
  // The ENROL step's single shape. Named for what it is, so it cannot be
  // mistaken for "the shape being answered".
  //
  // 83 §2 — SHAPE (c) ENROLS THE SAME CREDENTIAL, ASKED FOR DIFFERENTLY. Both
  // `webauthn` and `passwordless` run the passkey ceremony; what differs is that
  // shape (c) asks the server for a DISCOVERABLE one (`residentKey: 'required'`,
  // user verification required), because a passwordless sign-in names nobody.
  // §1's rule is why this is a flag and not a second ceremony: "shape (c) is the
  // SAME CREDENTIAL, offered without a password first."
  const isPasswordless = shape.id === "passwordless";
  const isPasskey = shape.id === "webauthn" || isPasswordless;
  // The CHALLENGE step's two controls, each offered on its own evidence. They
  // are not exclusive: a person holding both gets both, on one screen, which is
  // AUTH-1's one look and feel rather than a second modal.
  const canPasskey = offered.some((s) => s.id === "webauthn") && !!webauthn;
  const canCode = offered.some((s) => s.id === "totp");
  const tier = installationTier(cfg);

  useEffect(() => {
    if (step === "challenge" || step === "enrol") inputRef.current?.focus();
  }, [step, enrolment]);

  // The forced-enrolment path opens straight onto the square code, so the
  // secret is fetched as the modal mounts rather than behind another click.
  //
  // ── THE DEPENDENCY ARRAY IS [step, challenge] AND NOTHING ELSE ─────────────
  //
  // THE DEFECT THIS REPLACES, because it is worth being able to recognise: the
  // first version listed `busy` in the deps of the effect that SETS `busy`.
  // That is a closed loop with a fatal end —
  //
  //   effect runs (busy false) -> setBusy(true) -> re-render -> deps changed
  //   -> CLEANUP RUNS, setting live=false -> effect re-enters -> the `busy`
  //   guard returns early -> the in-flight promise resolves against the FIRST
  //   closure, whose `live` is now false, so NEITHER setEnrolment NOR
  //   setBusy(false) ever runs.
  //
  // `busy` is stuck true, `enrolment` stays null, and no square code is ever
  // drawn — on the one screen this whole brief exists to put in front of a
  // person. The server was never involved: `/totp/start` answered 200 the whole
  // time. Nothing could see it: 108 CI steps and 49 driven observations all
  // exercise the API, and §8 says in as many words that no instrument
  // photographs a modal. `scripts/lint/effect-sets-its-own-dep.js` now fails CI
  // on the shape rather than on this instance.
  //
  // `t` is gone from the deps too, and that is not tidying: `useTranslation()`
  // may hand back a new function identity on any render, which would re-run
  // this effect — and therefore re-mint a secret — for no reason at all. It is
  // read through a ref instead.
  //
  // The re-fetch guard is a REF rather than `enrolment`, for the same reason:
  // state in the deps is what re-enters the effect. It matters — stepping to
  // "about" and back flips `step`, and without the guard the person would be
  // handed a NEW secret after already scanning the old one.
  //
  // STRICTMODE IS ON (`src/main.tsx:148`), so this must survive a double mount.
  // It does: mount 1 fires, its cleanup sets live=false, mount 2 sees a ref
  // still empty (nothing has resolved) and fires again; whichever request
  // belongs to the live closure is the one that lands, and the other returns
  // early. `/totp/start` writes NO ROW, so a discarded one costs nothing.
  useEffect(() => {
    if (step !== "enrol") return undefined;
    if (enrolmentRef.current) return undefined;
    let live = true;
    setBusy(true);
    // 82 §4 — the same effect, the same guards, the same ref. A passkey's
    // "start" answers ceremony options where TOTP's answers a square code, and
    // nothing else about this changes: in particular `enrolPasskeyStart` WRITES
    // NO ROW either, so a discarded StrictMode double-mount costs nothing here
    // for the same reason it costs nothing there.
    (isPasskey ? enrolPasskeyStart(challenge, isPasswordless) : enrolTotpStart(challenge))
      .then((r) => {
        if (!live) return;
        enrolmentRef.current = r;
        setEnrolment(r);
      })
      .catch((e) => { if (live) setErr(readError(e, tRef.current)); })
      .finally(() => { if (live) setBusy(false); });
    return () => { live = false; };
    // `isPasskey` IS in the deps, and listing it is not a concession to the
    // linter. It is a boolean, so it is value-compared: it cannot re-enter this
    // effect on a new identity the way `t` could, and the one time it changes
    // is the one time the effect must run again — a different shape needs a
    // different `start`. The ref still guards the re-fetch.
  }, [step, challenge, isPasskey, isPasswordless]);

  // 82j, requirement 3 — WHAT CAN BE ASKED IN ADVANCE, ASK. One call, at mount,
  // and its answer decides what the button SAYS rather than whether it exists.
  // `capability` is set by this effect and therefore not in its own deps
  // (scripts/lint/effect-sets-its-own-dep.js, and the block above about `busy`).
  useEffect(() => {
    let live = true;
    passkeyCapability().then((c) => { if (live) setCapability(c); }).catch(() => {});
    return () => { live = false; };
  }, []);

  /**
   * Every WebAuthn failure becomes a WRITTEN SENTENCE — never a DOMException,
   * never an error name, never a browser string. 82i's class, third instance.
   *
   * `where` is the screen, because the same failure has a different next step
   * on each: at the challenge there are six digits and ten recovery codes on
   * this very screen to point at; at enrolment there is another shape to choose
   * instead.
   */
  const sayPasskeyFailure = (ex: unknown, where: "challenge" | "enrol") => {
    const kind: PasskeyFailure = classifyFailure(ex);
    // The two that are not faults. A cancel and "nothing could answer" are the
    // same DOMException by design (fingerprinting), so they are one message —
    // and a timeout is the same shape of non-event, said as a fact because our
    // own timer is what fired.
    const calm = kind === "not_completed" || kind === "timeout";

    let key: string;
    let fallback: string;
    if (where === "enrol") {
      key = kind === "timeout" ? "auth.passkey.enrol_timeout" : `auth.passkey.enrol_${kind}`;
      fallback = {
        timeout: "That took too long, so it was stopped. Press the button to try again.",
        not_completed: "No passkey was created. Press the button to try again, or set up an authenticator app instead.",
        already_enrolled: "This device already has a passkey for your account. Use that one, or set up a different device.",
        wrong_domain: "A passkey cannot be created from this address. Tell your firm's administrator which address you are using.",
        unsupported_device: "This device cannot create the kind of passkey we need. Try another device, or set up an authenticator app instead.",
        browser_too_old: "This browser is too old to create a passkey. Use a newer one, or set up an authenticator app instead.",
        failed: "The passkey could not be created. Press the button to try again.",
      }[kind];
    } else {
      // At the challenge screen the next step depends on what else he holds —
      // and both of those things are already on this screen.
      const next = canCode ? "code" : "recovery";
      key = calm ? `auth.passkey.use_${kind}_${next}` : `auth.passkey.use_${kind}`;
      fallback = calm
        ? (kind === "timeout"
          ? (canCode
            ? "That took too long, so it was stopped. Press the button to try again, or type the six digits from your authenticator app."
            : "That took too long, so it was stopped. Press the button to try again, or type one of your recovery codes.")
          : (canCode
            ? "No passkey was used. Press the button to try again, or type the six digits from your authenticator app."
            : "No passkey was used. Press the button to try again, or type one of your recovery codes."))
        : {
          already_enrolled: "That passkey cannot be used here. Try another way to sign in.",
          wrong_domain: "A passkey cannot be used from this address. Tell your firm's administrator which address you are using.",
          unsupported_device: "This device cannot use the passkey we are asking for. Try another way to sign in.",
          browser_too_old: "This browser is too old to use a passkey. Try another way to sign in.",
          failed: "The passkey could not be used. Try another way to sign in.",
          timeout: "", not_completed: "",
        }[kind];
    }

    const text = t(key, { fallback });
    if (calm) { setNote(text); setErr(""); } else { setErr(text); setNote(""); }
  };

  // ── 83 §5 (AUTH-14) — REMEMBER THIS BROWSER ────────────────────────────────
  //
  // OFF BY DEFAULT, and that is the whole posture: a standing bypass of the
  // second step is something a person CHOOSES, once, knowingly. A pre-ticked box
  // would make it the thing that happens to people who do not read.
  //
  // Offered on the CHALLENGE step only — never at enrolment — because what it
  // grants is permission to skip a step, and the server mints it only where a
  // step was actually passed.
  const [rememberBrowser, setRememberBrowser] = useState(false);
  // 83 §5 (glance) — THE PERSON NAMES THE BROWSER. Pre-filled from the server's
  // own `labelFor` (carried on the challenge response, so there is no second
  // implementation of it here to drift), editable, and IGNORING IT STILL WORKS:
  // an empty box falls back to the same suggestion server-side. It appears only
  // once the box is ticked — a field for a thing you have not chosen is noise —
  // and it never blocks the submit.
  const [deviceName, setDeviceName] = useState("");
  // 83's glance — what to CALL this passkey. Asked at enrolment, because that is
  // the one moment the person knows which device they are holding.
  const [passkeyName, setPasskeyName] = useState("");

  const submitChallenge = async (e?: React.FormEvent) => {
    e?.preventDefault?.();
    setErr("");
    if (!code.trim()) return;
    setBusy(true);
    try {
      if (!challenge) { setErr(readError({ code: "auth.challenge_expired" }, t)); return; }
      const res = await apiSecondStep(challenge, code.trim(), rememberBrowser, deviceName.trim());
      onDone?.(res.user, res.token, res);
    } catch (ex) {
      setErr(readError(ex, t));
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  const submitEnrolment = async (e?: React.FormEvent) => {
    e?.preventDefault?.();
    setErr("");
    if (!code.trim() || !enrolment?.ticket) return;
    setBusy(true);
    try {
      const res = await enrolTotpConfirm({
        ...(challenge ? { challenge } : {}),
        ticket: enrolment.ticket,
        code: code.trim(),
      });
      setCodes(res.recoveryCodes || []);
      setReplaced(res.replaced || 0);
      setSession(res.token ? res : null);
      setCode("");
      // 83 (glance) — NO CODES MEANS NO CODES SCREEN. The server answers `null`
      // when the person's existing sheet still stands (a second factor does not
      // replace it), and showing an empty ten-code page would be a ceremony
      // about nothing. He is simply done.
      if (res.recoveryCodes && res.recoveryCodes.length) setStep("codes");
      else onDone?.(res.user, res.token, res);
    } catch (ex) {
      setErr(readError(ex, t));
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  // ── 82 §4 — the two passkey submits ────────────────────────────────────────
  //
  // Neither takes a typed value, so neither is a form submit: the person presses
  // once and the browser takes over. Both end exactly where their typed twins
  // end — `onDone` with a session, or the ten codes — so the screen after this
  // one is the same screen.

  const submitPasskeyChallenge = async () => {
    setErr("");
    setNote("");
    if (!challenge) { setErr(readError({ code: "auth.challenge_expired" }, t)); return; }
    if (!webauthn) { setErr(readError({ code: "auth.invalid_credential" }, t)); return; }
    setBusy(true);
    try {
      const assertion = await assertPasskey(webauthn);
      const res = await secondStepPasskey(challenge, assertion, rememberBrowser, deviceName.trim());
      onDone?.(res.user, res.token, res);
    } catch (ex) {
      // NOTHING reaches `readError` from here: its fallthrough returns
      // `e.message`, and a DOMException's message is a raw browser string with
      // a specification URL in it. 82j, requirement 1.
      sayPasskeyFailure(ex, "challenge");
    } finally {
      setBusy(false);
    }
  };

  const submitPasskeyEnrolment = async () => {
    setErr("");
    setNote("");
    const start = enrolment as { ticket?: string; options?: unknown } | null;
    if (!start?.ticket || !start?.options) return;
    setBusy(true);
    try {
      const registration = await createPasskey(start.options as Parameters<typeof createPasskey>[0]);
      const res = await enrolPasskeyConfirm({
        ...(challenge ? { challenge } : {}),
        ticket: start.ticket,
        registration,
        // 83 (glance) — the authenticator's own answer about whether the
        // credential is discoverable, forwarded so the server records what it
        // DID rather than what was asked.
        ...((registration as { credProps?: { rk?: boolean } }).credProps
          ? { credProps: (registration as { credProps?: { rk?: boolean } }).credProps }
          : {}),
        // 83's glance — NAME IT AT ENROLMENT. `routes/authFactors.js` has
        // accepted a 60-character label since 82 and nothing ever sent one, so a
        // person with three passkeys read "Passkey, Passkey, Passkey" and could
        // not tell the Mac from the YubiKey from the Samsung. Blank still works:
        // the server falls back to its own default, exactly as before.
        ...(passkeyName.trim() ? { label: passkeyName.trim().slice(0, 60) } : {}),
      });
      setCodes(res.recoveryCodes || []);
      setReplaced(res.replaced || 0);
      setSession(res.token ? res : null);
      // Same as the TOTP path: no codes issued, no codes screen.
      if (res.recoveryCodes && res.recoveryCodes.length) setStep("codes");
      else onDone?.(res.user, res.token, res);
    } catch (ex) {
      sayPasskeyFailure(ex, "enrol");
    } finally {
      setBusy(false);
    }
  };

  const openAbout = () => { setCameFrom(step === "about" ? cameFrom : step); setStep("about"); };

  return (
    <div style={S.overlay} role="dialog" aria-modal="true">
      <div style={S.panel}>
        <div style={S.head}>
          <h2 style={S.title}>
            {step === "codes"
              ? t("auth.codes.title", { fallback: "Print these now" })
              : step === "about"
                ? (cameFrom === "challenge" && offered.length > 1
                    ? t("auth.about.title_many", { fallback: "How signing in works" })
                    : t(shape.nameKey, { fallback: isPasskey ? "Passkey" : "Authenticator app" }))
                : step === "enrol"
                  ? isPasskey
                    ? t("auth.enrol.title_passkey", { fallback: "Set up your passkey" })
                    : t("auth.enrol.title", { fallback: "Set up your authenticator app" })
                  : t("auth.challenge.title", { fallback: "Confirm it is you" })}
          </h2>
          {step !== "about" && (
            <button type="button" style={S.link} onClick={openAbout}>
              {t("auth.about.link", { fallback: "How does this work?" })}
            </button>
          )}
        </div>

        {/* At the challenge step, every shape he holds; at the enrol step, the
            one being enrolled. `cameFrom` is what says which. */}
        {step === "about" && (
          <About
            shapes={cameFrom === "challenge" && offered.length ? offered : [shape]}
            tier={tier}
            t={t}
            onBack={() => setStep(cameFrom)}
          />
        )}

        {step === "challenge" && (
          <form onSubmit={submitChallenge} noValidate style={S.form}>
            <p style={S.lede}>
              {canPasskey && canCode
                ? t("auth.challenge.lede_both", {
                    fallback:
                      "Use your passkey, or type the six digits your authenticator app is showing. Either one signs you in.",
                  })
                : canPasskey
                  ? t("auth.challenge.lede_passkey", {
                      fallback: "Use the passkey on this device to finish signing in.",
                    })
                  : t("auth.challenge.lede", {
                      fallback: "Open your authenticator app and type the six digits it is showing.",
                    })}
            </p>
            {/* THE INPUT COMES FIRST AND OWNS THE PRIMARY BUTTON. It stays on
                screen for every shape, because AUTH-9's ten recovery codes are
                about the ACCOUNT and not about the shape: a person who has lost
                his passkey types one here exactly as a person who has lost his
                phone does. An earlier draft substituted the passkey button FOR
                the input and stranded every passkey holder who lost his key. */}
            <input
              ref={inputRef}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputMode="text"
              autoComplete="one-time-code"
              // With TOTP held, six digits are the thing to type and a recovery
              // code is the fallback; with only a passkey, the input exists
              // SOLELY for a recovery code and says so.
              placeholder={canCode
                ? t("auth.challenge.placeholder", { fallback: "123 456" })
                : t("auth.challenge.placeholder_recovery", { fallback: "or a recovery code" })}
              style={S.codeInput}
            />
            {/* ── 82a's SECOND GLANCE — THE ONLY PRIMARY IS THE ONE THAT SENDS
                   WHAT YOU TYPED ─────────────────────────────────────────────
                The first cut put "Use my passkey" in `S.primary` ABOVE the
                field and gave the digits no visible button at all: it appeared
                only once something was typed. So the screen's one blue control
                sat at the top of a form and did something other than submit the
                form — and a blue button at the top of a form means submit. A
                person typed six digits and pressed it.

                ── 82k — ONE PRIMARY PER PATH, WHICH IS THE SHAPE THAT FITS ──
                The first repair made the passkey secondary. That answered the
                misread and understated the shape: THERE ARE TWO WAYS IN, and
                they are peers — AUTH-5 says any enrolled shape signs him in, so
                neither is the alternative to the other.

                So both are PRIMARY and both are SUBMITS, one per path, each
                directly under the thing it acts on. This does not reopen the
                ruling: what that forbids is a control MORE PROMINENT than the
                submit doing something other than submitting. Neither of these
                is more prominent than the other, and each submits its own path.

                And the bottom row holds CANCEL ALONE. A third button down there
                would duplicate the field's submit, which is the misread this
                whole sequence started from. */}
            <button type="submit" disabled={busy || !code.trim()} style={S.primary}>
              {busy
                ? t("auth.working", { fallback: "Checking…" })
                : canCode
                  ? t("auth.challenge.submit_code", { fallback: "Sign in with authenticator app" })
                  : t("auth.challenge.submit_recovery", { fallback: "Sign in with a recovery code" })}
            </button>

            <p style={S.hint}>
              {(() => {
                // Which thing was lost depends on what he holds, and with both
                // it is either. Three keys rather than a generic sentence,
                // because "lost your device" reads as neither.
                const suffix = canPasskey && canCode ? "_both" : canPasskey ? "_passkey" : "";
                const lost = canPasskey && canCode
                  ? "Lost your phone or your passkey?"
                  : canPasskey ? "Lost your passkey?" : "Lost your phone?";
                const n = recoveryRemaining?.remaining ?? 0;
                return n > 0
                  ? t(`auth.challenge.or_recovery_count${suffix}`, {
                      count: n, n, total: recoveryRemaining?.total ?? 0, // 209 §7 — « your last recovery code » at one
                      fallback: `${lost} Type one of your ${n} remaining recovery codes instead.`,
                    })
                  : t(`auth.challenge.or_recovery${suffix}`, {
                      fallback: `${lost} Type one of your printed recovery codes instead.`,
                    });
              })()}
            </p>
            {canPasskey && (
              <>
                <div style={S.orLine}>
                  <span style={S.orRule} />
                  <span style={S.orWord}>{t("common.or", { fallback: "or" })}</span>
                  <span style={S.orRule} />
                </div>
                {/* 82j, requirement 3 — NOT SILENTLY DISABLED. It is disabled
                    only where we KNOW nothing can work (no WebAuthn API at
                    all), and there the sentence beneath says why. Where the
                    machine merely has no built-in authenticator, a security key
                    or a phone can still answer, so the control stays live and
                    the line says what is needed. */}
                <button
                  type="button"
                  disabled={busy || capability === "unsupported"}
                  style={S.primaryWithGlyphs}
                  onClick={submitPasskeyChallenge}
                >
                  {busy ? (
                    t("auth.working", { fallback: "Checking…" })
                  ) : (
                    <>
                      {/* What will be asked of you, before the system asks it. */}
                      <FaceGlyph />
                      <FingerprintGlyph />
                      <span>{t("auth.challenge.submit_passkey", { fallback: "Sign in with passkey" })}</span>
                    </>
                  )}
                </button>
                {capability === "unsupported" && (
                  <p style={S.hint}>
                    {t("auth.passkey.unsupported_browser", {
                      fallback: "This browser cannot use passkeys. Sign in with the field above instead.",
                    })}
                  </p>
                )}
                {capability === "no-platform" && (
                  <p style={S.hint}>
                    {t("auth.passkey.no_platform", {
                      fallback: "This device has no built-in passkey. You can still use a security key, or your phone.",
                    })}
                  </p>
                )}
                {note && <p style={S.note}>{note}</p>}
              </>
            )}
            {/*
              83 §5 (AUTH-14) — REMEMBER THIS BROWSER.

              Below BOTH ways in, because it qualifies whichever of them the
              person uses and is not a third way in itself. OFF by default: a
              standing bypass of the second step is something a person chooses
              knowingly, and a pre-ticked box makes it the thing that happens to
              people who do not read.

              The label says THIRTY DAYS and says "this browser". Both are the
              honest words: `src/lib/deviceId.ts` is explicit that what is
              remembered survives "until you clear its data" and is not "your
              laptop", and a person deciding whether to skip a security step
              needs the window stated rather than implied.
            */}
            <label style={S.rememberRow}>
              <input
                type="checkbox"
                checked={rememberBrowser}
                onChange={(e) => {
                  setRememberBrowser(e.target.checked);
                  // Pre-fill on the way IN only, so a person who ticks, edits,
                  // unticks and re-ticks does not lose what they typed.
                  if (e.target.checked && !deviceName) setDeviceName(suggestedDeviceName || "");
                }}
                disabled={busy}
              />
              <span>
                {t("auth.challenge.remember_browser", {
                  days: 30,
                  fallback:
                    "Nobody except me has access to this computer, trust this browser on this device for 30 days. Name:",
                })}
              </span>
            </label>
            {/* ONE field, beside the box, and only once it is ticked. Not a
                second screen and not a required step: blank is a complete
                answer and the server falls back to the same suggestion. */}
            {rememberBrowser && (
              <input autoComplete="off" className="no-contact-autofill" /* 207 §18 */
                type="text"
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
                disabled={busy}
                maxLength={60}
                aria-label={t("auth.challenge.device_name", { fallback: "Name this browser" })}
                placeholder={t("auth.challenge.device_name", { fallback: "Name this browser" })}
                style={S.deviceNameInput}
              />
            )}
            {err && <div style={S.err}>{err}</div>}
            {/* CANCEL ALONE. Each path's submit sits under the path it belongs
                to; a third button here would duplicate the field's, which is
                the misread this sequence started from. */}
            {onCancel && (
              <div style={S.row}>
                <button type="button" style={S.ghost} onClick={onCancel}>
                  {t("common.cancel", { fallback: "Cancel" })}
                </button>
              </div>
            )}
          </form>
        )}

        {step === "enrol" && (
          <form onSubmit={submitEnrolment} noValidate style={S.form}>
            <p style={S.lede}>
              {isPasskey
                ? t("auth.enrol.lede_passkey", {
                    fallback:
                      "Create a passkey on this device. Your browser will ask you to confirm — with your fingerprint, your face, your screen lock or a security key.",
                  })
                : t("auth.enrol.lede", {
                    fallback:
                      "Scan this square code with an authenticator app on your phone, then type the six digits it starts showing.",
                  })}
            </p>
            {isPasskey ? (
              // No square code and nothing to type: the whole ceremony is one
              // press, and the browser draws the rest of it.
              <>
                <p style={S.hint}>
                  {busy
                    ? t("auth.working", { fallback: "Checking…" })
                    : capability === "unsupported"
                      ? t("auth.enrol.no_passkeys", {
                          fallback: "This browser cannot create a passkey. Use a different browser, or set up an authenticator app instead.",
                        })
                      : capability === "no-platform"
                        ? t("auth.passkey.no_platform", {
                            fallback: "This device has no built-in passkey. You can still use a security key, or your phone.",
                          })
                        : ""}
                </p>
                {/* The calm class on the enrolment screen too: a person who
                    dismissed the system dialog gets a line, not a red box. */}
                {note && <p style={S.note}>{note}</p>}
                {/* 83's glance — WHAT THE BROWSER IS ABOUT TO DO, BEFORE THE
                    BUTTON. Chrome offers the platform authenticator (Touch ID)
                    first and shows the device chooser only after a cancel. That
                    is Chrome's default and not ours to override — no
                    `authenticatorAttachment` is set, so the picker is already
                    reachable — but a person who wanted their phone cancels once
                    thinking the app ignored them. Said here rather than only in
                    the already-enrolled message, which arrives too late. */}
                <p style={S.hint}>
                  {t("auth.enrol.picker_hint", {
                    fallback:
                      "Your browser may offer this device first. For a phone or a security key, choose “use a phone or tablet” in the dialog.",
                  })}
                </p>
                {/* 83's glance — NAME IT HERE, at the one moment the person
                    knows which device they are holding. Blank is a complete
                    answer; the server falls back to its own default. A person
                    with a Mac, a YubiKey and a Samsung must not end up reading
                    "Passkey, Passkey, Passkey". */}
                <label style={S.rememberRow}>
                  <span>{t("auth.enrol.passkey_name", { fallback: "Name this passkey:" })}</span>
                </label>
                <input autoComplete="off" className="no-contact-autofill" /* 207 §18 */
                  type="text"
                  value={passkeyName}
                  onChange={(e) => setPasskeyName(e.target.value)}
                  disabled={busy}
                  maxLength={60}
                  placeholder={t("auth.enrol.passkey_name_hint", { fallback: "MacBook, YubiKey, Samsung…" })}
                  aria-label={t("auth.enrol.passkey_name", { fallback: "Name this passkey:" })}
                  style={{ ...S.deviceNameInput, marginLeft: 0, width: "100%" }}
                />
              </>
            ) : isSquareCode(enrolment) && enrolment.qr ? (
              <>
                <Square qr={enrolment.qr} />
                <details style={S.details}>
                  <summary style={S.summary}>
                    {t("auth.enrol.cannot_scan", { fallback: "Cannot scan it?" })}
                  </summary>
                  <p style={S.hint}>
                    {t("auth.enrol.type_key", {
                      fallback: "Type this key into the app by hand instead:",
                    })}
                  </p>
                  <code style={S.secret}>{isSquareCode(enrolment) ? enrolment.secret : ""}</code>
                </details>
              </>
            ) : (
              <p style={S.hint}>{busy ? t("auth.working", { fallback: "Checking…" }) : ""}</p>
            )}
            {!isPasskey && (
              <input
                ref={inputRef}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder={t("auth.challenge.placeholder", { fallback: "123 456" })}
                style={S.codeInput}
              />
            )}
            {err && <div style={S.err}>{err}</div>}
            <div style={S.row}>
              {onCancel && (
                <button type="button" style={S.ghost} onClick={onCancel}>
                  {t("common.cancel", { fallback: "Cancel" })}
                </button>
              )}
              {isPasskey ? (
                <button
                  type="button"
                  // Live unless we KNOW nothing can work. `no-platform` still
                  // leaves a security key and a phone, and the line above says so.
                  disabled={busy || !enrolment || capability === "unsupported"}
                  style={S.primary}
                  onClick={submitPasskeyEnrolment}
                >
                  {busy
                    ? t("auth.working", { fallback: "Checking…" })
                    : t("auth.enrol.create_passkey", { fallback: "Create my passkey" })}
                </button>
              ) : (
                <button type="submit" disabled={busy || !code.trim() || !enrolment} style={S.primary}>
                  {busy ? t("auth.working", { fallback: "Checking…" }) : t("auth.enrol.submit", { fallback: "Confirm" })}
                </button>
              )}
            </div>
          </form>
        )}

        {step === "codes" && (
          <div style={S.form}>
            {/* §4, Laurent's English, verbatim. */}
            <p style={S.lede}>
              {t("auth.codes.body", {
                fallback:
                  "These ten codes each work once, in place of your usual sign-in. They are the only way back into your account if you lose your device and nobody at your firm can help you. We cannot show them again: only a scrambled form is kept, so no one — including the publisher — can read them from the system. Store them where you will still have them when your phone is not in your hand.",
              })}
            </p>
            {/* ── 82a's GLANCE, FINDING 2 ────────────────────────────────────
                Issuing ten replaces the previous ten outright — one set per
                account, which is correct and was already true. What was missing
                is that NOBODY WAS TOLD: a person who had printed the old sheet
                walked away holding ten dead codes with no way to discover it.
                Shown only when something really was destroyed, so it can never
                claim a sheet died that never existed. */}
            {replaced > 0 && (
              <p style={S.warn}>
                {t("auth.codes.replaced", {
                  n: replaced,
                  fallback:
                    "These replace your previous recovery codes, which no longer work. If you printed them, destroy that sheet now.",
                })}
              </p>
            )}
            <ol style={S.codes}>
              {(codes || []).map((c) => (
                <li key={c} style={S.codeItem}>{c}</li>
              ))}
            </ol>
            <div style={S.row}>
              <button type="button" style={S.ghost} onClick={() => window.print()}>
                {t("auth.codes.print", { fallback: "Print" })}
              </button>
              <button
                type="button"
                style={S.primary}
                onClick={() => onDone?.(session?.user, session?.token, { recoveryCodes: codes })}
              >
                {t("auth.codes.stored", { fallback: "I have stored them" })}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * AUTH-11's four texts. Rendered from the shape table, so a shape that arrives
 * without them shows empty headings rather than silently showing nothing —
 * which is the difference between an omission somebody sees and one nobody does.
 */
function About({ shapes, tier, t, onBack }: AboutProps) {
  const many = shapes.length > 1;
  return (
    <div style={S.form}>
      {shapes.map((shape) => {
        const blocks = [
          { h: t("auth.about.how", { fallback: "How it works" }), k: shape.howKey },
          { h: t("auth.about.risks", { fallback: "Risks" }), k: shape.risksKey },
          { h: t("auth.about.fallback_firm", { fallback: "If you lose access" }), k: shape.fallbackFirmKey },
          {
            h: t("auth.about.fallback_seller", { fallback: "If your firm has no administrator available" }),
            k: sellerFallbackKey(shape, tier),
          },
        ];
        return (
          <div key={shape.id}>
            {/* The shape's own name, only when there is more than one to tell
                apart — otherwise the panel's title already said it. */}
            {many && <h3 style={S.h3}>{t(shape.nameKey, { fallback: shape.id })}</h3>}
            {blocks.map((b) => (
              <div key={b.k}>
                <h3 style={S.h3}>{b.h}</h3>
                <p style={S.body}>{t(b.k, { fallback: "" })}</p>
              </div>
            ))}
          </div>
        );
      })}
      <div style={S.row}>
        <button type="button" style={S.primary} onClick={onBack}>
          {t("common.back", { fallback: "Back" })}
        </button>
      </div>
    </div>
  );
}

/**
 * The square code, drawn as elements from the module matrix the API returned.
 *
 * ── IT IS SIZED BY THE PANEL, NOT BY A NUMBER (80 §glance) ──────────────────
 *
 * It was `width="212" height="212"` from brief 78 until Laurent's glance:
 * Microsoft Authenticator FAILED to read it and succeeded only on a second
 * attempt, Google's read it first time. An enrolment that needs two tries is a
 * support call.
 *
 * WHAT 212 ACTUALLY BOUGHT, measured against the real encoder rather than
 * estimated — and the second row is why a fixed number was the wrong shape:
 *
 *   account                          modules  span   at 212px   at 360px
 *   laurent.arnauts@wattlegal.be        49     57      3.72        6.32
 *   a long firm's address + issuer      61     69      3.07        5.22
 *
 * `n` GROWS WITH THE PAYLOAD — the account email and the firm name both ride in
 * the otpauth URI — so a fixed pixel width silently gives a firm with long
 * addresses a SMALLER module than the one that already failed. The size has to
 * be expressed as a width, not as a constant.
 *
 * So: fill the panel, capped. The panel is 420 wide with 24 of padding, so 372
 * is the content width and 360 leaves a margin; on a phone the cap never binds
 * and it takes whatever is there (~295 → 5.18, still well above the 3.72 that
 * failed). `height: auto` with the viewBox keeps it square at every width.
 */
function Square({ qr }: { qr: QrMatrix }) {
  const n = qr.size;
  const quiet = 4; // the standard quiet zone; without it many phones will not lock on
  const span = n + quiet * 2;
  return (
    <svg
      viewBox={`0 0 ${span} ${span}`}
      width="100%"
      role="img"
      style={{
        background: CT.c_fff,
        borderRadius: RAD.r8,
        display: "block",
        margin: "0 auto",
        maxWidth: 360,
        height: "auto",
      }}
    >
      <rect x="0" y="0" width={span} height={span} fill={CT.c_fff} />
      {qr.rows.map((row: string, r: number) =>
        [...row].map((cell, c) =>
          cell === "1" ? (
            <rect key={`${r}-${c}`} x={c + quiet} y={r + quiet} width="1" height="1" fill={CT.c_1B1B1B} />
          ) : null,
        ),
      )}
    </svg>
  );
}

/**
 * The server's `code` decides the sentence — never an English substring. The
 * same discipline LoginView already follows (Phase M+3.7).
 */
interface ApiError {
  code?: string;
  body?: { code?: string };
  message?: string;
}

function readError(ex: unknown, t: Translate): string {
  const e = (ex ?? {}) as ApiError;
  const code = e.code || e.body?.code || "";
  if (code === "auth.invalid_code") {
    return t("auth.err.invalid_code", { fallback: "That code is not right. Check your phone and try again." });
  }
  if (code === "auth.challenge_expired") {
    return t("auth.err.challenge_expired", { fallback: "This sign-in has expired. Close this and sign in again." });
  }
  if (code === "auth.enrolment_expired") {
    return t("auth.err.enrolment_expired", { fallback: "Setup has expired. Close this and start again." });
  }
  if (code === "generic.too_many_requests") {
    return t("auth.err.too_many", { fallback: "Too many attempts. Try again later." });
  }
  if (code === "auth.account_disabled") {
    return t("login.account_disabled", {
      fallback: "This account has been suspended or archived. Contact your firm's administrator.",
    });
  }
  return String(e.message || t("auth.err.generic", { fallback: "That did not work." }));
}

const S: Record<string, React.CSSProperties> = {
  overlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,.55)",
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: 16, zIndex: Z.banner,
  },
  panel: {
    width: "100%", maxWidth: 420, maxHeight: "90vh", overflowY: "auto",
    background: `var(--bc, ${CT.c_1a1d24})`,
    border: `1px solid var(--br, ${CT.c_2a2e37})`,
    borderRadius: RAD.r12, padding: 24,
    boxShadow: "0 10px 40px rgba(0,0,0,.5)",
    fontFamily: "'DM Sans', sans-serif",
  },
  head: { display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, marginBottom: 14 },
  title: { fontSize: FS.s18, fontWeight: 700, color: `var(--tp, ${CT.c_fff})`, margin: 0 },
  link: {
    background: "none", border: "none", color: `var(--ac, ${CT.c_4a7cff})`,
    fontSize: FS.s11, cursor: "pointer", padding: 0, fontFamily: "inherit",
    textDecoration: "underline", textUnderlineOffset: 2, whiteSpace: "nowrap",
  },
  form: { display: "flex", flexDirection: "column", gap: 14 },
  lede: { fontSize: FS.s13, color: `var(--ts, ${CT.c_d1d5db})`, margin: 0, lineHeight: 1.5 },
  body: { fontSize: FS.s12, color: `var(--ts, ${CT.c_d1d5db})`, margin: "4px 0 0", lineHeight: 1.55 },
  h3: { fontSize: FS.s12, fontWeight: 700, color: `var(--tp, ${CT.c_fff})`, margin: 0, textTransform: "uppercase", letterSpacing: ".04em" },
  hint: { fontSize: FS.s11, color: `var(--tm, ${CT.c_9ca3af})`, margin: 0, lineHeight: 1.5 },
  // 83 §5 — the remember-this-browser row. A real <label> wrapping the box, so
  // the words are part of the hit target rather than decoration beside it.
  // 83 §5 (glance) — the name box. Indented under the checkbox it belongs to,
  // so it reads as part of that choice rather than as a new question.
  deviceNameInput: {
    marginLeft: 24,
    marginTop: 2,
    padding: "6px 9px",
    background: `var(--bd, ${CT.c_0f1115})`,
    color: `var(--tp, ${CT.c_fff})`,
    border: `1px solid var(--br, ${CT.c_2a2e37})`,
    borderRadius: RAD.r6,
    fontSize: FS.s12,
    fontFamily: "inherit",
    outline: "none",
    width: "calc(100% - 24px)",
  } as const,
  rememberRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: FS.s12,
    color: `var(--tm, ${CT.c_9ca3af})`,
    cursor: "pointer",
    marginTop: 4,
  } as const,
  codeInput: {
    padding: "11px 13px", background: `var(--bd, ${CT.c_0f1115})`,
    color: `var(--tp, ${CT.c_fff})`, border: `1px solid var(--br, ${CT.c_2a2e37})`,
    borderRadius: RAD.r7, fontSize: FS.s18, fontFamily: "'JetBrains Mono', monospace",
    letterSpacing: ".18em", textAlign: "center", outline: "none",
  },
  err: {
    fontSize: FS.s12, color: `var(--rd, ${CT.c_f87171})`,
    background: "rgba(248,113,113,.08)", border: "1px solid rgba(248,113,113,.3)",
    borderRadius: RAD.r6, padding: "8px 10px",
  },
  // 82a's glance, finding 2. Same shell as `err` — it is not an error, but it
  // IS the one line on this screen a person must not skim, and the sheet in
  // their hand stops working because of it. Borrowing the shape rather than
  // inventing a third is AUTH-1's one look and feel.
  warn: {
    fontSize: FS.s12, color: `var(--rd, ${CT.c_f87171})`,
    background: "rgba(248,113,113,.08)", border: "1px solid rgba(248,113,113,.3)",
    borderRadius: RAD.r6, padding: "8px 10px", margin: 0, lineHeight: 1.5,
  },
  // 82a's second glance — the "or" that says the control below is an
  // ALTERNATIVE to the field above, not its submit. A rule either side so it
  // reads as a divider rather than a word floating in the form.
  orLine: { display: "flex", alignItems: "center", gap: 10, margin: "2px 0" },
  orRule: { flex: 1, height: 1, background: `var(--br, ${CT.c_2a2e37})` },
  orWord: {
    fontSize: FS.s11, color: `var(--tm, ${CT.c_9ca3af})`,
    textTransform: "uppercase", letterSpacing: ".06em",
  },
  // 82k — the passkey's primary. The SAME `primary` in every visual respect;
  // it differs only by laying out the two glyphs beside the label, because
  // neither path may look more important than the other.
  primaryWithGlyphs: {
    padding: "10px 16px", background: `var(--ac, ${CT.c_4a7cff})`, color: CT.c_fff,
    border: "none", borderRadius: RAD.r6, fontSize: FS.s12, fontWeight: 600,
    cursor: "pointer", display: "inline-flex", alignItems: "center",
    justifyContent: "center", gap: 8,
  },
  // 82j — the CALM class. A cancelled prompt is not a fault and must not look
  // like one; `err` stays red for things that actually went wrong.
  note: {
    fontSize: FS.s12, color: `var(--ts, ${CT.c_d1d5db})`,
    background: `var(--bd, ${CT.c_0f1115})`, border: `1px solid var(--br, ${CT.c_2a2e37})`,
    borderRadius: RAD.r6, padding: "8px 10px", margin: 0, lineHeight: 1.5,
  },
  row: { display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 },
  primary: {
    padding: "10px 16px", background: `var(--ac, ${CT.c_4a7cff})`, color: CT.c_fff,
    border: "none", borderRadius: RAD.r8, fontSize: FS.s13, fontWeight: 600,
    cursor: "pointer", fontFamily: "inherit",
  },
  ghost: {
    padding: "10px 16px", background: "none", color: `var(--tm, ${CT.c_9ca3af})`,
    border: `1px solid var(--br, ${CT.c_2a2e37})`, borderRadius: RAD.r8,
    fontSize: FS.s13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
  },
  details: { fontSize: FS.s11 },
  summary: { color: `var(--ac, ${CT.c_4a7cff})`, cursor: "pointer", fontSize: FS.s11 },
  secret: {
    display: "block", marginTop: 6, padding: "8px 10px",
    background: `var(--bd, ${CT.c_0f1115})`, color: `var(--tp, ${CT.c_fff})`,
    border: `1px solid var(--br, ${CT.c_2a2e37})`, borderRadius: RAD.r6,
    fontFamily: "'JetBrains Mono', monospace", fontSize: FS.s12, wordBreak: "break-all",
  },
  codes: {
    margin: 0, padding: "12px 14px 12px 34px",
    background: `var(--bd, ${CT.c_0f1115})`,
    border: `1px solid var(--br, ${CT.c_2a2e37})`, borderRadius: RAD.r8,
    display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 14px",
  },
  codeItem: {
    fontFamily: "'JetBrains Mono', monospace", fontSize: FS.s12,
    color: `var(--tp, ${CT.c_fff})`, letterSpacing: ".04em",
  },
};
